// The two label makers, described as data: page size, layouts, how a label is built, the size line, the PDF format and file
// names. The editor widget draws either from this.
import * as sticker from '../lib/sticker';
import * as letter from '../lib/letter';
import type { DrawOptions, LabelData, PageDims } from './types';

export interface LayoutChoice { key: string; label: string; cells: { w: number; h: number }[]; cols: number }

export interface PageSpec {
  kind: 'sticker' | 'letter';
  title: string;              // browser tab
  subtitle: string;           // the page title in the top bar
  layoutTitle: string;
  layouts: LayoutChoice[];
  hasOrientation: boolean;
  margin: number; gap: number;
  count: (layout: string) => number;
  dims: (o: DrawOptions) => PageDims;
  build: (v: LabelData, labelW: number, labelH: number, qrSrc: string | null, n: number, o: DrawOptions) => string;
  previewTitle: (o: DrawOptions) => string;
  meta: (n: number, d: PageDims, o: DrawOptions) => string;
  landscape: (o: DrawOptions) => boolean;
  pdfFormat: (o: DrawOptions) => { format: 'letter' | [number, number]; size: [number, number] };
  printSize: (o: DrawOptions) => string;
  fileName: string;           // without extension
  nameMax?: number; descMax?: number; // the sticker's input limits
}

const inches = (px: number) => (px / 96).toFixed(2);

// Which 2-product split is offered on the 4×6: the sticker always splits across its long side.
export const SPLIT_FOR = { portrait: '2v', landscape: '2h' } as const;

export const STICKER: PageSpec = {
  kind: 'sticker',
  title: "4×6 Sticker — Uncle Wiener's Label Studio",
  subtitle: '4 × 6 Sticker',      // page title
  layoutTitle: 'Products per Sticker',
  layouts: [
    { key: '1', label: '1', cols: 1, cells: [{ w: 20, h: 20 }] },
    { key: '2v', label: '2 ↑↓', cols: 1, cells: [{ w: 20, h: 9 }, { w: 20, h: 9 }] },
    { key: '2h', label: '2 ↔', cols: 2, cells: [{ w: 9, h: 20 }, { w: 9, h: 20 }] },
  ],
  hasOrientation: true,
  margin: sticker.MARGIN, gap: sticker.GAP,
  count: (layout) => { const [c, r] = sticker.LAYOUT_GRID[layout]!; return c * r; },
  dims: (o) => sticker.getLabelDims(o.layout, o.orient),
  build: sticker.buildLabel,
  previewTitle: (o) => `Sticker Preview — ${o.orient === 'landscape' ? '6″ × 4″' : '4″ × 6″'}`,
  meta: (n, d) => `${n} product${n > 1 ? 's' : ''} · each ${inches(d.labelW)}″ × ${inches(d.labelH)}″`,
  landscape: (o) => o.orient === 'landscape',
  // 4" × 6" sticker = 101.6 mm × 152.4 mm
  pdfFormat: (o) => { const s: [number, number] = o.orient === 'landscape' ? [152.4, 101.6] : [101.6, 152.4]; return { format: s, size: s }; },
  printSize: (o) => (o.orient === 'landscape' ? '6in 4in' : '4in 6in'),
  fileName: 'sticker-4x6',
  nameMax: 180, descMax: 200,
};

export const LETTER: PageSpec = {
  kind: 'letter',
  title: "Letter Sheets — Uncle Wiener's Label Studio",
  subtitle: 'Letter Sheets',
  layoutTitle: 'Labels per Page',
  layouts: [
    { key: '1', label: '1', cols: 1, cells: [{ w: 18, h: 18 }] },
    { key: '2', label: '2', cols: 1, cells: [{ w: 18, h: 8 }, { w: 18, h: 8 }] },
    { key: '4', label: '4', cols: 2, cells: Array(4).fill({ w: 9, h: 7 }) },
    { key: '8', label: '8', cols: 2, cells: Array(8).fill({ w: 9, h: 7 }) },
  ],
  hasOrientation: false,
  margin: letter.MARGIN, gap: letter.GAP,
  count: (layout) => Number(layout),
  dims: (o) => letter.getLabelDims(Number(o.layout)),
  build: letter.buildLabel,
  previewTitle: () => 'Print Preview — US Letter 8.5″ × 11″',
  meta: (n, d) => `${n} label${n > 1 ? 's' : ''} · ${d.cols}×${d.rows} · each ${inches(d.labelW)}″ × ${inches(d.labelH)}″${n === 4 ? ' · landscape' : ''}`,
  landscape: (o) => o.layout === '4',
  pdfFormat: (o) => ({ format: 'letter', size: o.layout === '4' ? [279.4, 215.9] : [215.9, 279.4] }),
  printSize: (o) => `letter ${o.layout === '4' ? 'landscape' : 'portrait'}`,
  fileName: 'print-labels',
};
